import { DATEPICKER_FORMAT } from 'constants/dates'
import { parseISO } from 'date-fns'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { IColorByValueAttributeMap } from 'app/services/api/colorByValue'
import { CBVSimplifiedType } from 'common/utils/cbvUtils'
import { limitDecimals } from 'common/utils/numbers'

import {
  generateColorRangeForDateType,
  generateColorRangeForNumberType
} from 'features/DataManagement/Components/ColorByValue/utils/cbvColorMapGenerator'
import { getHexCodesFromCBVTheme } from 'features/DataManagement/Components/ColorByValue/utils/cbvThemes'

import { IColorByValueCustomizeColorModalInnerContent } from './types'

// REVIEW: This function now seems to be redundent, since the
//         type check is now made much simpler
const getTypeFromCBVAttributeMap = (
  attributeMap: IColorByValueAttributeMap
): CBVSimplifiedType => {
  const value = attributeMap.configurations[0]?.value ?? {}
  let res: CBVSimplifiedType = attributeMap.attributeType

  if (value.number != null) {
    res = 'number'
  } else if (value.boolean != null) {
    res = 'boolean'
  } else if (value.date != null) {
    res = 'date'
  }

  return res
}

const attributeClasses =
  'flex-1 font-medium text-gray-500 leading-none tracking-[0.03em]'

const ColorThresholdCustomizer = ({
  attributeMap,
  setAttributeMap,
  setDataErrors,
  reset
}: IColorByValueCustomizeColorModalInnerContent) => {
  const attributeType = getTypeFromCBVAttributeMap(attributeMap)

  const onColorChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    colorIdx: number
  ) => {
    const color = evt.target.value
    const colorMapClone = [...attributeMap.configurations]
    const previousEl = colorMapClone[colorIdx]

    colorMapClone.splice(colorIdx, 1, {
      ...previousEl,
      color
    })

    setAttributeMap({
      ...attributeMap,
      configurations: colorMapClone
    })
  }

  const onNumberUpdate = (
    evt: React.ChangeEvent<HTMLInputElement>,
    dataIdx: number,
    minMaxType: 'min' | 'max'
  ) => {
    const val = evt.target.value.replace(/[^\d.-]/g, '')
    const isValidNumber = /^-?\d*(\.\d{0,2})?$/.test(val)

    if (!isValidNumber) {
      // Ignore input action
      return
    }

    const decimalNumber = val as unknown as number
    const colorMapClone = [...attributeMap.configurations]
    const previousEl = colorMapClone[dataIdx]

    if (previousEl.value.number == null) {
      return
    }

    colorMapClone.splice(dataIdx, 1, {
      ...previousEl,
      value: {
        number: {
          min:
            minMaxType === 'min' ? decimalNumber : previousEl.value.number.min,
          max:
            minMaxType === 'max' ? decimalNumber : previousEl.value.number.max
        }
      }
    })

    setAttributeMap({
      ...attributeMap,
      configurations: colorMapClone
    })
  }

  const onDateUpdate = (
    evt: Date | null,
    dataIdx: number,
    minMaxType: 'minDate' | 'maxDate'
  ) => {
    if (!evt) {
      return
    }

    const colorMapClone = [...attributeMap.configurations]
    const previousEl = colorMapClone[dataIdx]

    if (previousEl.value.date == null) {
      return
    }

    if (attributeMap.configurations.length < 1) {
      return <div>No predefined options available</div>
    }

    try {
      colorMapClone.splice(dataIdx, 1, {
        ...previousEl,
        value: {
          date: {
            minDate:
              minMaxType === 'minDate'
                ? evt.toISOString().split('T')[0]
                : previousEl.value.date.minDate,
            maxDate:
              minMaxType === 'maxDate'
                ? evt.toISOString().split('T')[0]
                : previousEl.value.date.maxDate
          }
        }
      })
    } catch (e) {
      console.error(e)
    }

    setAttributeMap({
      ...attributeMap,
      configurations: colorMapClone
    })
  }

  const increaseOrDecreaseRange = (delta: number) => {
    let newRange = Math.round(attributeMap.configurations.length + delta)
    const isYearValue = attributeMap.attributeCode.includes('Year')

    if (newRange < 1) {
      newRange = 1
    }

    setAttributeMap({
      ...attributeMap,
      configurations: generateColorRangeForNumberType(
        getHexCodesFromCBVTheme(attributeMap.iconTheme),
        attributeMap.attributeTypeRange.number?.min ?? 0,
        attributeMap.attributeTypeRange.number?.max ?? 10000,
        newRange,
        isYearValue
      )
    })
  }

  const increaseOrDecreaseDateRange = (delta: number) => {
    let newRange = Math.round(attributeMap.configurations.length + delta)
    const firstDateRange = attributeMap.configurations[0]?.value.date
    const lastDateRange = attributeMap.configurations.at(-1)?.value.date

    if (newRange < 1) {
      newRange = 1
    }

    setAttributeMap({
      ...attributeMap,
      configurations: generateColorRangeForDateType(
        getHexCodesFromCBVTheme(attributeMap.iconTheme),
        firstDateRange?.minDate ??
          attributeMap.attributeTypeRange.date?.minDate ??
          new Date().toISOString().split('T')[0],
        lastDateRange?.maxDate ??
          attributeMap.attributeTypeRange.date?.maxDate ??
          new Date().toISOString().split('T')[0],
        newRange
      )
    })
  }

  return (
    <>
      <div className='mb-6 flex justify-between font-semibold leading-none'>
        <div>Thresholds</div>
        <button
          className='px-2 font-semibold uppercase leading-none tracking-[0.02em] text-gray-500'
          onClick={reset}
        >
          Reset
        </button>
      </div>
      <div className='mb-3 flex uppercase text-gray-700'>
        <div
          className={[
            'mr-3 w-[90px] font-medium',
            'leading-none tracking-[0.03em] text-gray-500'
          ].join(' ')}
        >
          Color
        </div>
        {attributeType === 'number' && (
          <>
            <div className={attributeClasses}>From</div>
            <div className='invisible mx-1'>-</div>
            <div className={attributeClasses}>To</div>
          </>
        )}
        {['string', 'boolean'].includes(attributeType) && (
          <div className={attributeClasses}>Value</div>
        )}
      </div>
      {attributeMap.configurations.map((segment, idx) => (
        <div
          key={`${segment.color}-${idx}`}
          className='mb-1 flex items-center rounded-sm'
        >
          <div className='mr-3 w-[90px]'>
            <label
              className={[
                'flex w-full items-center justify-between px-2 py-1',
                'rounded border border-gray-300',
                'focus-within:border-lighthouseBlue'
              ].join(' ')}
            >
              <input
                type='color'
                className='w-6 bg-transparent'
                value={segment.color}
                onChange={(evt) => onColorChange(evt, idx)}
              />
              <ArrowDropDown />
            </label>
          </div>
          {attributeType === 'number' && segment.value.number != null && (
            <>
              <div className='flex-1'>
                <input
                  type='text'
                  inputMode='numeric'
                  size={10}
                  className='w-full rounded-sm'
                  value={limitDecimals(segment.value.number.min)}
                  onChange={(evt) => onNumberUpdate(evt, idx, 'min')}
                />
              </div>
              <div className='mx-1'>–</div>
              <div className='flex-1'>
                <input
                  type='text'
                  inputMode='numeric'
                  size={10}
                  className='w-full rounded-sm'
                  value={limitDecimals(segment.value.number.max)}
                  onChange={(evt) => onNumberUpdate(evt, idx, 'max')}
                />
              </div>
            </>
          )}
          {attributeType === 'date' && segment.value.date != null && (
            <>
              <div className='flex-1'>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    className='w-full'
                    format={DATEPICKER_FORMAT}
                    value={parseISO(segment.value.date?.minDate)}
                    onChange={(e) => onDateUpdate(e, idx, 'minDate')}
                    onError={(error) => {
                      const keyPrefix = `${attributeMap.attributeId}-${idx}`

                      setDataErrors?.((prev) => ({
                        ...prev,
                        [`${keyPrefix}-minDate`]: !!error
                      }))
                    }}
                  />
                </LocalizationProvider>
              </div>
              <div className='mx-1'>–</div>
              <div className='flex-1'>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    className='w-full'
                    format={DATEPICKER_FORMAT}
                    value={parseISO(segment.value.date?.maxDate)}
                    onChange={(e) => onDateUpdate(e, idx, 'maxDate')}
                    onError={(error) => {
                      const keyPrefix = `${attributeMap.attributeId}-${idx}`

                      setDataErrors?.((prev) => ({
                        ...prev,
                        [`${keyPrefix}-maxDate`]: !!error
                      }))
                    }}
                  />
                </LocalizationProvider>
              </div>
            </>
          )}
          {attributeType === 'boolean' && segment.value.boolean != null && (
            <div className='flex-1'>
              {segment.value.boolean ? 'True' : 'False'}
            </div>
          )}
          {attributeType === 'string' && segment.value.string != null && (
            <div className='flex-1'>{segment.value.string.name}</div>
          )}
        </div>
      ))}
      {attributeType === 'number' && (
        <div className='my-4 flex gap-x-2'>
          <button
            className='mx-2 py-1 font-medium text-lighthouseBlue'
            onClick={() => increaseOrDecreaseRange(1)}
          >
            Add Range
          </button>
          <button
            className='mx-2 py-1 font-medium text-gray-700'
            onClick={() => increaseOrDecreaseRange(-1)}
          >
            Remove Last Range
          </button>
        </div>
      )}
      {attributeType === 'date' && (
        <div className='my-4 flex gap-x-2'>
          <button
            className='mx-2 py-1 font-medium text-lighthouseBlue'
            onClick={() => increaseOrDecreaseDateRange(1)}
          >
            Add Range
          </button>
          <button
            className='mx-2 py-1 font-medium text-gray-700'
            onClick={() => increaseOrDecreaseDateRange(-1)}
          >
            Remove Last Range
          </button>
        </div>
      )}
    </>
  )
}

export default ColorThresholdCustomizer
